// Per-project work item type management section — rendered inside ProjectSettingsPanel.
// Features: list (name, color swatch, default badge), add (name, color, is_default),
// inline-edit (name, color, set-as-default), delete with confirm.
// 409 on delete-default surfaces a toast instead of crashing.

import { useState } from "preact/hooks";
import { Plus, Trash2 } from "lucide-preact";
import type { ApiWorkItemType } from "@juuplan/shared";
import { COLOR_PRESETS } from "../lib/format";
import { toastError, toastSuccess } from "../lib/toast";
import {
  useWorkItemTypes,
  addWorkItemType,
  patchWorkItemType,
  removeWorkItemType,
} from "../store/workItemTypes";
import { ApiError } from "../lib/api";

import { Button } from "./ui/Button";

interface Props {
  projectId: number;
  /** When false the mutating controls are hidden (view-only role). */
  canEdit: boolean;
}

interface EditState {
  name: string;
  color: string;
  is_default: boolean;
  prefix: string;
}

/**
 * Per-project work item type management list.
 * Renders inside ProjectSettingsPanel as an accordion-style section.
 */
export function WorkItemTypesSection(props: Props): preact.JSX.Element {
  const { projectId, canEdit } = props;
  const { items, loading, error } = useWorkItemTypes(projectId);

  // Row currently being edited (id -> draft)
  const [editing, setEditing] = useState<Map<number, EditState>>(new Map());
  // Rows pending a save
  const [saving, setSaving] = useState<Set<number>>(new Set());
  // Rows pending a delete confirm
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());

  // New-type form state
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string>(COLOR_PRESETS[0]);
  const [newIsDefault, setNewIsDefault] = useState(false);
  const [newPrefix, setNewPrefix] = useState("");
  const [creating, setCreating] = useState(false);

  // ---- helpers ----

  const startEdit = (t: ApiWorkItemType): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.set(t.id, {
        name: t.name,
        color: t.color,
        is_default: t.is_default,
        prefix: t.prefix ?? "",
      });
      return next;
    });
  };

  const cancelEdit = (id: number): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const setDraft = (id: number, patch: Partial<EditState>): void => {
    setEditing((prev) => {
      const cur = prev.get(id);
      if (!cur) return prev;
      const next = new Map(prev);
      next.set(id, { ...cur, ...patch });
      return next;
    });
  };

  const commitEdit = async (t: ApiWorkItemType): Promise<void> => {
    const draft = editing.get(t.id);
    if (!draft) return;
    setSaving((prev) => new Set(prev).add(t.id));
    try {
      await patchWorkItemType(t.id, projectId, {
        name: draft.name.trim() || t.name,
        color: draft.color,
        is_default: draft.is_default,
        prefix: draft.prefix.trim(),
      });
      cancelEdit(t.id);
      toastSuccess("Typ gespeichert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
    }
  };

  const handleDelete = async (t: ApiWorkItemType): Promise<void> => {
    setSaving((prev) => new Set(prev).add(t.id));
    try {
      await removeWorkItemType(t.id, projectId);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
      toastSuccess("Typ gelöscht.");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toastError("Standard-Typ kann nicht gelöscht werden. Bitte zuerst einen anderen Typ als Standard setzen.");
      }
      // other errors already toasted by the api wrapper
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
    }
  };

  const submitNew = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await addWorkItemType(projectId, {
        name,
        color: newColor,
        is_default: newIsDefault,
        prefix: newPrefix.trim() || undefined,
      });
      setNewName("");
      setNewColor(COLOR_PRESETS[0]);
      setNewIsDefault(false);
      setNewPrefix("");
      setShowNew(false);
      toastSuccess("Typ angelegt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  // ---- render ----

  return (
    <div class="flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Aufgaben-Typen
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">
          Fehler: {error}
        </p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Aufgaben-Typen.
        </p>
      )}

      {!loading && items.map((t) => {
        const draft = editing.get(t.id);
        const isSaving = saving.has(t.id);
        const isConfirmDel = confirmDel.has(t.id);

        return (
          <div
            key={t.id}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
          >
            {/* Row header */}
            <div class="flex items-center gap-2 px-2 py-1.5">
              {/* Color swatch */}
              <span
                class="inline-block size-4 rounded-full shrink-0 border border-[color:var(--color-outline-variant)]"
                style={{ background: draft ? draft.color : t.color }}
                aria-hidden="true"
              />

              {/* Name */}
              <span class="flex-1 text-xs font-medium text-[color:var(--color-on-surface)] truncate">
                {draft ? draft.name || t.name : t.name}
              </span>

              {/* Ticket prefix chip */}
              {(draft ? draft.prefix : t.prefix) && (
                <span class="shrink-0 font-mono text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-1.5 py-0.5">
                  {(draft ? draft.prefix : t.prefix ?? "").toUpperCase()}-#
                </span>
              )}

              {/* Default badge */}
              {(draft ? draft.is_default : t.is_default) && (
                <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-1.5 py-0.5">
                  Standard
                </span>
              )}

              {/* Actions (edit role only) */}
              {canEdit && !draft && (
                <div class="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(t)}
                    class="rounded px-1.5 py-0.5 text-label text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    Umbenennen
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.add(t.id);
                        return next;
                      })
                    }
                    disabled={isSaving}
                    class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                    aria-label="Typ löschen"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* Inline editor */}
            {draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 pb-2 pt-2 flex flex-col gap-2">
                <input
                  type="text"
                  value={draft.name}
                  maxLength={100}
                  onInput={(e) =>
                    setDraft(t.id, { name: (e.target as HTMLInputElement).value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void commitEdit(t); }
                    if (e.key === "Escape") cancelEdit(t.id);
                  }}
                  placeholder="Name"
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <label class="flex flex-col gap-1">
                  <span class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                    Ticket-Präfix
                  </span>
                  <input
                    type="text"
                    value={draft.prefix}
                    maxLength={6}
                    onInput={(e) =>
                      setDraft(t.id, {
                        prefix: (e.target as HTMLInputElement).value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, ""),
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); void commitEdit(t); }
                      if (e.key === "Escape") cancelEdit(t.id);
                    }}
                    placeholder="z. B. BUG (leer = keine Ticketnummer)"
                    class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 font-mono text-xs text-[color:var(--color-on-surface)] placeholder:font-sans placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                    aria-label="Ticket-Präfix"
                  />
                </label>
                <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setDraft(t.id, { color: c })}
                      class={`size-5 rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                        draft.color === c
                          ? "border-[color:var(--color-on-surface)] scale-110"
                          : "border-transparent hover:scale-110"
                      }`}
                      style={{ background: c }}
                      aria-label={`Farbe ${c}`}
                      aria-pressed={draft.color === c}
                    />
                  ))}
                </div>
                <label class="inline-flex items-center gap-2 text-xs text-[color:var(--color-on-surface)] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={draft.is_default}
                    onChange={(e) =>
                      setDraft(t.id, { is_default: (e.target as HTMLInputElement).checked })
                    }
                    class="rounded border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus-visible:ring-[color:var(--color-primary)]"
                  />
                  Als Standard setzen
                </label>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() => cancelEdit(t.id)}
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="primary"
                    size="xs"
                    type="button"
                    onClick={() => void commitEdit(t)}
                    disabled={isSaving || !draft.name.trim()}
                  >
                    {isSaving ? "Speichere…" : "Speichern"}
                  </Button>
                </div>
              </div>
            )}

            {/* Delete confirm */}
            {isConfirmDel && !draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1.5">
                <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                  Typ „{t.name}" wirklich löschen?
                </p>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.delete(t.id);
                        return next;
                      })
                    }
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="destructive"
                    size="xs"
                    type="button"
                    onClick={() => void handleDelete(t)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Lösche…" : "Löschen"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add new type */}
      {canEdit && (
        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
          {!showNew ? (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="w-full inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <Plus size={14} aria-hidden="true" />
              Typ hinzufügen
            </button>
          ) : (
            <div class="px-2 py-2 flex flex-col gap-2">
              <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Neuer Typ
              </div>
              <input
                type="text"
                value={newName}
                maxLength={100}
                onInput={(e) =>
                  setNewName((e.target as HTMLInputElement).value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void submitNew(); }
                  if (e.key === "Escape") {
                    setShowNew(false);
                    setNewName("");
                  }
                }}
                placeholder="Name des Typs"
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Name des neuen Typs"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <label class="flex flex-col gap-1">
                <span class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                  Ticket-Präfix
                </span>
                <input
                  type="text"
                  value={newPrefix}
                  maxLength={6}
                  onInput={(e) =>
                    setNewPrefix(
                      (e.target as HTMLInputElement).value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void submitNew(); }
                    if (e.key === "Escape") {
                      setShowNew(false);
                      setNewName("");
                      setNewPrefix("");
                    }
                  }}
                  placeholder="leer = automatisch aus Name"
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 font-mono text-xs text-[color:var(--color-on-surface)] placeholder:font-sans placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Ticket-Präfix des neuen Typs"
                />
              </label>
              <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    class={`size-5 rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                      newColor === c
                        ? "border-[color:var(--color-on-surface)] scale-110"
                        : "border-transparent hover:scale-110"
                    }`}
                    style={{ background: c }}
                    aria-label={`Farbe ${c}`}
                    aria-pressed={newColor === c}
                  />
                ))}
              </div>
              <label class="inline-flex items-center gap-2 text-xs text-[color:var(--color-on-surface)] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newIsDefault}
                  onChange={(e) =>
                    setNewIsDefault((e.target as HTMLInputElement).checked)
                  }
                  class="rounded border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus-visible:ring-[color:var(--color-primary)]"
                />
                Als Standard setzen
              </label>
              <div class="flex items-center gap-1.5 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowNew(false);
                    setNewName("");
                  }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={() => void submitNew()}
                  disabled={!newName.trim() || creating}
                >
                  {creating ? "Lege an…" : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
